import {
  MouseEvent, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Store, store, useAppDispatch } from 'src/redux/Store';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import FormSingleSelect, { SelectItem } from 'src/components/Control/FormControls/FormSingleSelect';
import Subtitle from 'src/components/UI/Subtitle';
import Header from 'src/components/UI/Header';
import Row from 'src/components/UI/Row';
import Col2 from 'src/components/UI/Col2';
import Amount from 'src/components/UI/Amount';
import Title from 'src/components/UI/Title';
import Button from '@material-ui/core/Button';
import Form from 'src/components/UI/Form';
import Scroller from 'src/components/UI/Scroller';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { t, f } from 'src/lib/language';
import ContactSelector from 'src/components/Control/ContactSelector';
import MetadataSelector, { MetadataType } from 'src/components/Control/MetadataSelector';
import FundraisingSelector from 'src/components/Control/FundraisingSelector';
import AmbassadorSelector from 'src/components/Control/AmbassadorSelector';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import { getContactAction } from 'src/redux/actions/contactActions';
import { ContactPayload } from 'src/models/ContactModel';
import { getContactInfoForInvoice } from 'src/lib/ContactHelper';
import { getProductListAction } from 'src/redux/actions/productAction';
import {
  getAccountsAction,
  getDepartmentsAction,
  getProgramsAction,
} from 'src/redux/actions/dataActions';
import { ProductList, ProductModel } from 'src/models/ProductModel';
import OrdersDisplayer from 'src/components/Elements/OrderDisplayer';
import ManageProductsButton from 'src/components/Control/Buttons/ManageProductsButton';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import OrganisationBankAccountSelector from 'src/components/Control/OrganisationBankAccountSelector';
import PaymentAccountSelector from 'src/components/Control/PaymentAccountSelector';
import { ProcessingType } from 'src/models/GatewayModel';
import { PAYMENT_TYPE } from 'src/models/PaymentModel';
import { MODALS_SIZE } from 'src/models/modal';
import { closeModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import {
  SUBSCRIPTION_FREQUENCY,
  SUBSCRIPTION_FREQUENCY_LIST,
  SubscriptionPayload,
} from 'src/models/SubscriptionModel';
import {
  InvoiceModel,
  OrderList,
  OrderModel,
  OrderTaxModel,
  ProductOrderList,
  ProductOrderModel,
  TicketInfo,
} from 'src/models/InvoiceModel';
import { INVOICE_FIELDS, INVOICE_ORDER_FIELDS } from 'src/constants/Invoices';
import { getInvoiceAction } from 'src/redux/actions/invoiceActions';
import { postSubscriptionAction } from 'src/redux/actions/subscriptionActions';
import SubscriptionValidator from 'src/validations/SubscriptionValidator';
import InvoiceOrderValidator from 'src/validations/InvoiceOrderValidator';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import SaveIcon from '@material-ui/icons/Save';
import { Moment } from 'moment';
import { isEmpty, keyBy } from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';

const AddSubscriptionForm = () => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const taxes = useSelector((state: Store) => {
    if (!state.taxes) return {};
    const result = {} as { [key: number]: any };
    state.taxes.forEach((tax) => {
      if (tax.id) result[tax.id] = tax;
    });
    return result;
  });
  const products = useSelector((state: Store) => state.products || [] as ProductList);
  const countries = useSelector(
    (state: Store) => keyBy(state.data.countries ?? [], 'id'),
  );
  const [contactId, setContactId] = useState(0);
  const { contact: contactInfo } = useSelector(
    (state: Store) => {
      if (!!state.contacts[contactId]) {
        return state.contacts[contactId] as ContactPayload;
      }
      return {} as ContactPayload;
    },
  );

  const sourceInvoiceId = useSelector((state: Store) => {
    const modal = state.modals[MODALS.addSubscription];
    if (!modal || !modal.payload) return undefined;
    return modal.payload.sourceInvoiceId as number | undefined;
  });

  const [subscriptionState, setSubscriptionState] = useState({
    title: '',
    frequency: SUBSCRIPTION_FREQUENCY.monthly,
    start_date: moment() as Moment,
    end_date: null as Moment | null,
    contact_id: 0,
    contact_first_name: '',
    contact_last_name: '',
    contact_company_name: '',
    contact_salutation_id: 0,
    seller_id: 0,
    contact_payer_id: 0,
    create_invoice: true,
    create_payment: false,
    send_invoice_by_email: true,
    issue_tax_receipt: false,
    note: '',
    program_id: 0,
    department_id: 0,
    account_id: 0,
    is_linked: false,
    event_id: 0,
    ambassador_id: 0,
    billing_address: '',
    billing_suite: '',
    billing_city: '',
    billing_state: '',
    billing_country_id: 0,
    billing_zip_code: '',
    shipping_address: '',
    shipping_suite: '',
    shipping_city: '',
    shipping_state: '',
    shipping_country_id: 0,
    shipping_zip_code: '',
    payment_type: PAYMENT_TYPE.cc,
    payment_info_id: 0,
    payment_gateway_id: 0,
    bank_account_id: 0,
  });

  const [orderState, setOrderState] = useState([{
    product_id: 0,
    paid: 0,
    quantity: 1,
    deductible: 0,
    discount: 0,
    taxes: {} as OrderTaxModel,
  }] as ProductOrderList);

  useEffect(() => {
    dispatch(setError({ [FORM.add_subscription]: {} }));
    dispatch(getDepartmentsAction());
    dispatch(getAccountsAction());
    dispatch(getProgramsAction());
    dispatch(getProductListAction());
  }, []);

  useEffect(() => {
    if (!contactInfo) return;
    const info = getContactInfoForInvoice(contactInfo);
    setSubscriptionState((prev) => ({
      ...prev,
      contact_salutation_id: info.contact_salutation_id || 0,
      contact_first_name: info.contact_first_name || '',
      contact_last_name: info.contact_last_name || '',
      contact_company_name: info.contact_company_name || '',
      billing_address: info.billing_address || '',
      billing_suite: info.billing_suite || '',
      billing_city: info.billing_city || '',
      billing_state: info.billing_state || '',
      billing_country_id: info.billing_country_id || 0,
      billing_zip_code: info.billing_zip_code || '',
      shipping_address: info.shipping_address || '',
      shipping_suite: info.shipping_suite || '',
      shipping_city: info.shipping_city || '',
      shipping_state: info.shipping_state || '',
      shipping_country_id: info.shipping_country_id || 0,
      shipping_zip_code: info.shipping_zip_code || '',
      issue_tax_receipt: !!info.issue_tax_receipt,
    }));
  }, [contactInfo]);

  useEffect(() => {
    if (!sourceInvoiceId) return;
    (async () => {
      await dispatch(getInvoiceAction(sourceInvoiceId));
      const invoice = store.getState().invoices[sourceInvoiceId] as InvoiceModel;
      if (!invoice?.id) return;
      setContactId(invoice.contact_id ?? 0);
      setSubscriptionState((prev) => ({
        ...prev,
        title: invoice.title || '',
        contact_id: invoice.contact_id ?? 0,
        seller_id: invoice.seller_id || 0,
        contact_payer_id: invoice.contact_payer_id || 0,
        program_id: invoice.program_id || 0,
        department_id: invoice.department_id || 0,
        account_id: invoice.account_id || 0,
        note: invoice.note || '',
      }));
      if (invoice.order?.length) {
        setOrderState(invoice.order);
      }
    })();
  }, [sourceInvoiceId]);

  const processing = useSelector(
    (state: Store) => state.formStatus[FORM.add_subscription] === FORM_STATUS.processing,
  );

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const frequencyItems: SelectItem[] = SUBSCRIPTION_FREQUENCY_LIST.map(
    (frequency) => ({ id: frequency, text: frequency }),
  );

  const paymentTypeItems: SelectItem[] = [
    { id: PAYMENT_TYPE.cc, text: PAYMENT_TYPE.cc },
    { id: PAYMENT_TYPE.eft, text: PAYMENT_TYPE.eft },
  ];

  const getGatewayType = (paymentType: PAYMENT_TYPE) => (
    paymentType === PAYMENT_TYPE.cc ? ProcessingType.cc : ProcessingType.eft
  );

  const onFieldChange = useCallback((value: Value, field: string) => {
    setSubscriptionState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onContactChange = useCallback((id: number) => {
    setContactId(id);
    setSubscriptionState((prev) => ({ ...prev, contact_id: id }));
    dispatch(getContactAction(id, FORM.add_subscription));
  }, [dispatch]);

  const onDateChange = (field: string) => (value: MaterialUiPickersDate) => {
    setSubscriptionState((prev) => ({
      ...prev,
      [field]: value ? (value as Moment) : null,
    }));
  };

  const onCreatePaymentChange = (value: boolean) => {
    setSubscriptionState((prev) => ({ ...prev, create_payment: value }));
    if (value) {
      const selectedOrder = orderState.find((order) => !!order.product_id);
      const product = selectedOrder
        ? products.find((p) => p.id === selectedOrder.product_id)
        : undefined;
      if (product) {
        setSubscriptionState((prev) => ({
          ...prev,
          bank_account_id: product.bank_account_id || 0,
          payment_gateway_id: prev.payment_type === PAYMENT_TYPE.cc
            ? product.payment_gateway_cc_id || 0
            : product.payment_gateway_eft_id || 0,
        }));
      }
    }
  };

  const getTaxesFromProduct = (product: ProductModel) => {
    const taxesAmount = {} as OrderTaxModel;
    if (!product.taxes) return taxesAmount;
    product.taxes.forEach((taxId: number) => {
      taxesAmount[taxId] = 0;
      if (taxes[taxId]) {
        const price = Number(product.price ?? 0);
        const discount = Number(product.discount ?? 0);
        taxesAmount[taxId] = taxes[taxId].rate * (price - discount);
      }
    });
    return taxesAmount;
  };

  const getTaxesForOrder = (order: ProductOrderModel): string => JSON.stringify(order.taxes);

  const denormalizeOrders = (): OrderList => orderState.map((order: ProductOrderModel) => ({
    ...order,
    deductible: (order.deductible ?? 0) / 100,
    taxes: getTaxesForOrder(order),
  } as OrderModel));

  const formatOrdersForValidation = (orders: ProductOrderList) => {
    const result = {} as Record<string, any>;
    orders.forEach((order: ProductOrderModel, index: number) => {
      result[`deductible_${index}`] = order.deductible;
      result[`discount_${index}`] = order.discount;
      result[`note_${index}`] = order.note;
      result[`paid_${index}`] = order.paid;
      result[`product_id_${index}`] = order.product_id;
      result[`quantity_${index}`] = order.quantity;
      result[`taxes_${index}`] = order.taxes;
      result[`tickets_${index}`] = order.tickets || [];
    });
    return result;
  };

  const getTaxItem = (id: number) => taxes[id];

  const getInvoiceTaxes = (orderList: ProductOrderList) => {
    const invoicesTaxes: OrderTaxModel = {};
    orderList.forEach((product: ProductOrderModel) => {
      if (product.taxes) {
        Object.keys(product.taxes).forEach((taxId) => {
          if (!taxes[taxId]) return;
          if (!invoicesTaxes[taxId]) invoicesTaxes[taxId] = 0;
          const paid = Number(product.paid ?? 0);
          const discount = Number(product.discount ?? 0);
          const quantity = Number(product.quantity ?? 0);
          invoicesTaxes[taxId] += (quantity * (paid - discount)) * Number(taxes[taxId].rate);
        });
      }
    });
    return invoicesTaxes;
  };

  const calculatedSubtotal = useMemo(() => {
    let subtotal = 0;
    orderState.forEach((order) => {
      subtotal += ((order.quantity ?? 0) * ((order.paid ?? 0) - (order.discount ?? 0)));
    });
    return subtotal;
  }, [orderState]);

  const calculatedTaxes = useMemo(() => {
    const taxesObject = getInvoiceTaxes(orderState);
    let totalTaxes = 0;
    Object.keys(taxesObject).forEach((taxId) => {
      totalTaxes += Number(taxesObject[taxId]);
    });
    return totalTaxes;
  }, [orderState, taxes]);

  const calculatedTotal = useMemo(() => (
    calculatedSubtotal + calculatedTaxes),
  [calculatedSubtotal, calculatedTaxes]);

  const getTaxPercentage = (rate: number) => `${Math.ceil(rate * 100000) / 1000}%`;

  const renderTaxes = () => {
    const taxesObject = getInvoiceTaxes(orderState);
    return (
      <>
        { Object.keys(taxesObject).map((taxId: string, index) => {
          const tax = getTaxItem(Number(taxId));
          const abbreviation = tax[`abbreviation_${lang}`];
          return (
            <div
              key={`tax-item-${index}`}
              style={{ textAlign: 'right' }}>
              <span id={`tax-item-${abbreviation}`}>
                {tax[`name_${lang}`]} ({getTaxPercentage(tax.rate)}):&nbsp;
              </span>
              <Amount>{formatter.format(taxesObject[taxId])}</Amount>
            </div>
          );
        }) }
      </>
    );
  };

  const onAddProductOrder = useCallback(() => {
    setOrderState((prevOrderState) => [
      ...prevOrderState,
      {
        product_id: 0,
        paid: 0,
        quantity: 1,
        deductible: 0,
      },
    ]);
  }, []);

  const onDeleteProduct = useCallback((orderIndex: number) => {
    setOrderState((prev) => {
      const productOrderList = [...prev];
      productOrderList.splice(orderIndex, 1);
      return productOrderList;
    });
  }, []);

  const onUpdateTickets = useCallback((tickets: TicketInfo[], index: number) => {
    setOrderState((prev) => {
      const orderList = [...prev];
      orderList[index] = { ...orderList[index], tickets };
      return orderList;
    });
  }, []);

  const onUpdateProduct = useCallback((value: Value, field: string, index: number) => {
    setOrderState((prev) => {
      const orderList = [...prev];
      orderList[index][field] = value;
      return orderList;
    });
  }, []);

  const onProductChange = useCallback((product?: ProductModel, index?: number) => {
    if (index === undefined) return;
    setOrderState((prev) => {
      const productOrderList = [...prev];
      if (!product) {
        productOrderList[index][INVOICE_ORDER_FIELDS.product_id] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.paid] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.discount] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.deductible] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.taxes] = {} as OrderTaxModel;
        return productOrderList;
      }
      productOrderList[index][INVOICE_ORDER_FIELDS.product_id] = product?.id || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.paid] = product?.price || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.discount] = product?.discount || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.deductible] = (product?.deductible || 0) * 100;
      productOrderList[index][INVOICE_ORDER_FIELDS.taxes] = getTaxesFromProduct(product);
      return productOrderList;
    });
    if (product) {
      setSubscriptionState((prev) => ({
        ...prev,
        title: product[`name_${lang}`],
        program_id: product.program_id || 0,
        department_id: product.department_id || 0,
        account_id: product.account_id || 0,
        bank_account_id: product.bank_account_id || 0,
        payment_gateway_id: prev.payment_type === PAYMENT_TYPE.cc
          ? product.payment_gateway_cc_id || 0
          : product.payment_gateway_eft_id || 0,
      }));
    }
  }, [taxes, lang]);

  const renderContactDetails = () => {
    if (isEmpty(contactInfo)) return '';
    return (
      <div style={{ marginTop: '16px' }}>
        <Row style={{ marginBottom: '8px' }}>
          <Col2>
            <b>Billing Address:</b>
            <div> { contactInfo?.billing_address || '' } </div>
            <div> { contactInfo?.billing_suite || '' }</div>
            <div> { contactInfo?.billing_city || '' }</div>
            <div> { contactInfo?.billing_state || '' }</div>
            <div> { contactInfo?.billing_zip_code || '' }</div>
            <div>{ countries[contactInfo.billing_country_id ?? 0]?.name_en || '' }</div>
          </Col2>
          <Col2>
            <b>Shipping Address:</b>
            <div> { contactInfo?.shipping_address || '' } </div>
            <div> { contactInfo?.shipping_suite || '' } </div>
            <div> { contactInfo?.shipping_city || '' } </div>
            <div> { contactInfo?.shipping_state || '' } </div>
            <div> { contactInfo?.shipping_zip_code || '' }</div>
            <div> { countries[contactInfo?.shipping_country_id ?? 0]?.name_en || '' }</div>
          </Col2>
        </Row>
        <Col2>
          <div> <b>Tel:</b> { contactInfo?.phone_mobile || '' } (Mobile) </div>
          <div> <b>Email:</b> { contactInfo?.email || '' } </div>
        </Col2>
      </div>
    );
  };

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.addSubscription }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.add_subscription]: {} }));
    const errorBag = {} as ErrorBag;

    try {
      SubscriptionValidator(lang, subscriptionState.create_invoice).validateSync(
        {
          ...subscriptionState,
          start_date: subscriptionState.start_date?.format('YYYY-MM-DD'),
          end_date: subscriptionState.end_date ? subscriptionState.end_date.format('YYYY-MM-DD') : null,
        },
        { abortEarly: false },
      );
    } catch (validationErrors: any) {
      (validationErrors?.inner || []).forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
    }

    try {
      InvoiceOrderValidator(orderState, lang).validateSync(
        formatOrdersForValidation(orderState),
        { abortEarly: false },
      );
    } catch (validationErrors: any) {
      (validationErrors?.inner || []).forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
    }

    if (Object.keys(errorBag).length) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: t(lang, 'forms.contact.validation_errors'),
      }));
      dispatch(setError({ [FORM.add_subscription]: errorBag }));
      return false;
    }

    const payload = {
      title: subscriptionState.title,
      frequency: subscriptionState.frequency,
      start_date: subscriptionState.start_date.format('YYYY-MM-DD'),
      end_date: subscriptionState.end_date ? subscriptionState.end_date.format('YYYY-MM-DD') : null,
      create_invoice: subscriptionState.create_invoice,
      create_payment: subscriptionState.create_payment,
      send_invoice_by_email: subscriptionState.send_invoice_by_email,
      payment_type: subscriptionState.create_payment ? subscriptionState.payment_type : undefined,
      payment_info_id: subscriptionState.create_payment
        ? subscriptionState.payment_info_id || null
        : undefined,
      payment_gateway_id: subscriptionState.create_payment
        ? subscriptionState.payment_gateway_id || null
        : undefined,
      currency_id: null,
      bank_account_id: subscriptionState.create_payment
        ? subscriptionState.bank_account_id || null
        : undefined,
      invoice: subscriptionState.create_invoice
        ? {
          contact_id: subscriptionState.contact_id || null,
          contact_first_name: subscriptionState.contact_first_name,
          contact_last_name: subscriptionState.contact_last_name,
          contact_company_name: subscriptionState.contact_company_name,
          title: subscriptionState.title,
          issued_date: subscriptionState.start_date.format('YYYY-MM-DD'),
          due_date: subscriptionState.start_date.format('YYYY-MM-DD'),
          contact_salutation_id: subscriptionState.contact_salutation_id || null,
          seller_id: subscriptionState.seller_id || null,
          contact_payer_id: subscriptionState.contact_payer_id || null,
          note: subscriptionState.note || null,
          program_id: subscriptionState.program_id || null,
          department_id: subscriptionState.department_id || null,
          account_id: subscriptionState.account_id || null,
          issue_tax_receipt: subscriptionState.issue_tax_receipt,
          billing_address: subscriptionState.billing_address,
          billing_suite: subscriptionState.billing_suite,
          billing_city: subscriptionState.billing_city,
          billing_state: subscriptionState.billing_state,
          billing_country_id: subscriptionState.billing_country_id || null,
          billing_zip_code: subscriptionState.billing_zip_code,
          shipping_address: subscriptionState.shipping_address,
          shipping_suite: subscriptionState.shipping_suite,
          shipping_city: subscriptionState.shipping_city,
          shipping_state: subscriptionState.shipping_state,
          shipping_country_id: subscriptionState.shipping_country_id || null,
          shipping_zip_code: subscriptionState.shipping_zip_code,
          event: subscriptionState.is_linked
            ? {
              event_id: subscriptionState.event_id || null,
              ambassador_id: subscriptionState.ambassador_id || null,
            }
            : undefined,
        }
        : undefined,
      order: subscriptionState.create_invoice ? denormalizeOrders() : undefined,
    } as SubscriptionPayload;

    dispatch(postSubscriptionAction(payload, true));
    return true;
  };

  return (
    <>
      <Header height="86px">
        <Row>
          <Title>{t(lang, 'menus.add_subscription')}</Title>
          <Button
            style={{ marginLeft: 'auto' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </Row>
        <Row>
          <ActionButton
            id="subscription-save"
            loading={processing}
            category={MENU_BUTTON_CATEGORY.action}
            onClick={handleSubmit}>
            <SaveIcon />
          </ActionButton>
          <ManageProductsButton />
        </Row>
      </Header>
      <Scroller style={{ paddingRight: '16px', overflowX: 'hidden' }}>
        <Form maxWidth={MODALS_SIZE.large}>
          <Row height="72px" alignItems="flex-start">
            <FormTextField
              form={FORM.add_subscription}
              label={f(lang, 'recurrings.title')}
              name="title"
              onChange={onFieldChange}
              required
              value={subscriptionState.title || ''} />
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormSingleSelect
                form={FORM.add_subscription}
                label={f(lang, 'recurrings.frequency')}
                name="frequency"
                items={frequencyItems}
                value={subscriptionState.frequency}
                noMarginTop
                onChange={(value: SUBSCRIPTION_FREQUENCY) => onFieldChange(value, 'frequency')} />
            </Col2>
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormDatePicker
                form={FORM.add_subscription}
                required
                label={f(lang, 'recurrings.start_date')}
                name="start_date"
                onChange={onDateChange('start_date')}
                value={subscriptionState.start_date} />
            </Col2>
            <Col2 minHeight="52px">
              <FormDatePicker
                form={FORM.add_subscription}
                label={f(lang, 'recurrings.end_date')}
                name="end_date"
                onChange={onDateChange('end_date')}
                value={subscriptionState.end_date} />
            </Col2>
          </Row>
          <Subtitle noMargin style={{ marginBottom: '8px', marginTop: '16px' }}>
            {t(lang, 'forms.invoices.contacts')}
          </Subtitle>
          <Row height="72px" alignItems="flex-start" style={{ marginBottom: '16px' }}>
            <Col2 minHeight="52px">
              <ContactSelector
                id="subscription-contact-selector"
                autocompleteId={AUTOCOMPLETE_ID.add_subscription_contact}
                onChange={(id: number) => onContactChange(id)}
                label={f(lang, 'recurrings.contact_id')}
                form={FORM.add_subscription}
                name="contact_id"
                contactId={subscriptionState.contact_id}
                hideEdit={!subscriptionState.contact_id} />
            </Col2>
          </Row>
          <Subtitle noMargin style={{ marginBottom: '16px' }}>
            {t(lang, 'forms.invoices.orders')}
          </Subtitle>
          <OrdersDisplayer
            form={FORM.add_subscription}
            onUpdateProduct={onUpdateProduct}
            orders={orderState}
            onDeleteProduct={onDeleteProduct}
            onAddProduct={onAddProductOrder}
            onProductChange={onProductChange}
            fundraisingLinked={false}
            onUpdateTickets={onUpdateTickets} />
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              {t(lang, 'forms.invoices.subtotal')}:&nbsp;
              <Amount>{formatter.format(calculatedSubtotal)}</Amount>
            </span>
          </Row>
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              <i>{renderTaxes()}</i>
            </span>
          </Row>
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              <b>
                Total:&nbsp;
                <Amount>{formatter.format(calculatedTotal)}</Amount>
              </b>
            </span>
          </Row>
          <Subtitle noMargin style={{ marginBottom: '8px', marginTop: '16px' }}>
            {f(lang, 'recurrings.state')}
          </Subtitle>
          <Row height="auto">
            <FormCheckbox
              name="create_invoice"
              label={f(lang, 'recurrings.create_invoice')}
              checked={subscriptionState.create_invoice}
              onChange={onFieldChange} />
          </Row>
          <Row height="auto">
            <FormCheckbox
              name="create_payment"
              label={f(lang, 'recurrings.create_payment')}
              checked={subscriptionState.create_payment}
              onChange={(value: boolean) => onCreatePaymentChange(value)} />
          </Row>
          <Row height="auto">
            <FormCheckbox
              name="send_invoice_by_email"
              label={f(lang, 'recurrings.send_invoice_by_email')}
              checked={subscriptionState.send_invoice_by_email}
              onChange={onFieldChange} />
          </Row>
          { subscriptionState.create_invoice && (
            <>
              <Row height="72px" alignItems="flex-start" style={{ marginTop: '16px' }}>
                <Col2 minHeight="52px">
                  <ContactSelector
                    id="subscription-seller-selector"
                    autocompleteId={AUTOCOMPLETE_ID.add_invoice_seller}
                    onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.seller_id)}
                    label={t(lang, 'forms.invoices.seller')}
                    form={FORM.add_subscription}
                    name={INVOICE_FIELDS.seller_id}
                    contactId={subscriptionState.seller_id}
                    hideEdit={!subscriptionState.seller_id} />
                </Col2>
                <Col2 minHeight="52px">
                  <ContactSelector
                    id="subscription-payer-selector"
                    autocompleteId={AUTOCOMPLETE_ID.add_invoice_payer}
                    onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.contact_payer_id)}
                    label={t(lang, 'forms.invoices.payer')}
                    form={FORM.add_subscription}
                    name={INVOICE_FIELDS.contact_payer_id}
                    contactId={subscriptionState.contact_payer_id}
                    hideEdit={!subscriptionState.contact_payer_id} />
                </Col2>
              </Row>
              {renderContactDetails()}
              <Subtitle noMargin style={{ marginBottom: '8px', marginTop: '16px' }}>
                Fundraising
              </Subtitle>
              <Row height="auto">
                <FormCheckbox
                  name="is_linked"
                  label="Link this invoice to a fundraising"
                  checked={!!subscriptionState.is_linked}
                  onChange={onFieldChange} />
              </Row>
              { subscriptionState.is_linked && (
                <Row height="72px" alignItems="flex-start">
                  <Col2 minHeight="52px">
                    <FundraisingSelector
                      form={FORM.add_subscription}
                      label="Fundraising"
                      name="event_id"
                      value={subscriptionState.event_id}
                      onChange={(id) => onFieldChange(id, 'event_id')} />
                  </Col2>
                  <Col2 minHeight="52px">
                    <AmbassadorSelector
                      form={FORM.add_subscription}
                      fundraisingId={subscriptionState.event_id}
                      label="Ambassador"
                      value={subscriptionState.ambassador_id}
                      name="ambassador_id"
                      onChange={(id) => onFieldChange(id, 'ambassador_id')} />
                  </Col2>
                </Row>
              )}
              <Row height="auto">
                <FormCheckbox
                  name={INVOICE_FIELDS.issue_tax_receipt}
                  label={t(lang, 'forms.invoices.issue_tax_receipt')}
                  checked={!!subscriptionState.issue_tax_receipt}
                  onChange={onFieldChange} />
              </Row>
              <Subtitle noMargin style={{ marginBottom: '8px' }}>
                Meta Data
              </Subtitle>
              <Row>
                <Col2 minHeight="52px">
                  <MetadataSelector
                    id="subscription-program-selector"
                    type={MetadataType.program}
                    onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.program_id)}
                    form={FORM.add_subscription}
                    name={INVOICE_FIELDS.program_id}
                    value={subscriptionState.program_id || undefined}
                    noMarginTop
                    margin="none" />
                </Col2>
                <Col2 minHeight="52px">
                  <MetadataSelector
                    id="subscription-department-selector"
                    type={MetadataType.department}
                    onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.department_id)}
                    form={FORM.add_subscription}
                    name={INVOICE_FIELDS.department_id}
                    value={subscriptionState.department_id || undefined}
                    noMarginTop
                    margin="none" />
                </Col2>
              </Row>
              <Row>
                <Col2 minHeight="52px">
                  <MetadataSelector
                    id="subscription-account-selector"
                    type={MetadataType.account}
                    onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.account_id)}
                    form={FORM.add_subscription}
                    name={INVOICE_FIELDS.account_id}
                    value={subscriptionState.account_id || undefined}
                    noMarginTop
                    margin="none" />
                </Col2>
              </Row>
              <Row>
                <FormTextField
                  id="subscription-note"
                  form={FORM.add_subscription}
                  label={t(lang, 'forms.invoices.note')}
                  multiline
                  minRows="4"
                  name={INVOICE_FIELDS.note}
                  onChange={onFieldChange}
                  value={subscriptionState.note || ''} />
              </Row>
            </>
          )}
          { subscriptionState.create_payment && (
            <>
              <Subtitle noMargin style={{ marginBottom: '8px' }}>
                {t(lang, 'forms.invoices.payment')}
              </Subtitle>
              <Row height="72px" alignItems="flex-start">
                <Col2 minHeight="52px">
                  <FormSingleSelect
                    form={FORM.add_subscription}
                    label={f(lang, 'recurrings.payment_info')}
                    name="payment_type"
                    items={paymentTypeItems}
                    value={subscriptionState.payment_type}
                    noMarginTop
                    onChange={(value: PAYMENT_TYPE) => onFieldChange(value, 'payment_type')} />
                </Col2>
                <Col2 minHeight="52px">
                  <PaymentAccountSelector
                    value={subscriptionState.payment_info_id || undefined}
                    type={subscriptionState.payment_type}
                    onChange={(id: number) => onFieldChange(id, 'payment_info_id')}
                    form={FORM.add_subscription}
                    name="payment_info_id"
                    gatewayId={subscriptionState.payment_gateway_id || 0}
                    contactId={subscriptionState.contact_id || 1} />
                </Col2>
              </Row>
              <Row height="72px" alignItems="flex-start" style={{ marginTop: '8px' }}>
                <Col2 minHeight="52px">
                  <OrganisationBankAccountSelector
                    value={subscriptionState.bank_account_id || undefined}
                    onChange={(id: number) => onFieldChange(id, 'bank_account_id')}
                    form={FORM.add_subscription}
                    name="bank_account_id"
                    noMarginTop
                    noMinWidth />
                </Col2>
                <Col2 minHeight="52px">
                  <GatewaySelector
                    form={FORM.add_subscription}
                    name="payment_gateway_id"
                    onChange={(value: number) => onFieldChange(value, 'payment_gateway_id')}
                    type={getGatewayType(subscriptionState.payment_type)}
                    value={subscriptionState.payment_gateway_id}
                    noMarginTop
                    noMinWidth />
                </Col2>
              </Row>
            </>
          )}
        </Form>
      </Scroller>
    </>
  );
};

export default AddSubscriptionForm;
